// AdminUi.jsx
//
// The furniture every admin list screen needs: a page shell, a search box, a
// table that knows its own loading/error/empty states, pills, row actions and
// a modal.
//
// ── WHY THESE EXIST ──
// Eleven screens were drawing the same things from the same inherited
// skeleton, each restating the 36x36 icon button with its two inline hover
// handlers, the avatar circle, the "no rows" block and the modal shell. When
// the brand colours moved, some of them moved. These are the one place now.
//
// Styling lives in styles/portal-ui.css. Nothing here carries a hex literal:
// a component that hardcodes a colour is a component that will be wrong after
// the next rebrand, and the last one left eleven copies behind.

import React, { useEffect } from "react";
import "../../../styles/portal-ui.css";

/* ── page shell ──────────────────────────────────────────────────────────── */

export function PageShell({ title, count, search, actions, children }) {
  return (
    <div className="ui-page">
      <div className="ui-page__top">
        <div>
          <h1 className="ui-page__title">{title}</h1>
          {count != null ? <p className="ui-page__count">{count}</p> : null}
        </div>
        <div className="ui-page__tools">
          {search ? (
            <Search
              value={search.value}
              onChange={search.onChange}
              placeholder={search.placeholder}
            />
          ) : null}
          {actions}
        </div>
      </div>
      {children}
    </div>
  );
}

export function Search({ value, onChange, placeholder = "Search…" }) {
  return (
    <div className="ui-search">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M10.6 2.4a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4Zm0 2.2a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm5.9 11.3 4.8 4.8a1.2 1.2 0 0 1-1.7 1.7l-4.8-4.8a9.6 9.6 0 0 0 1.7-1.7Z" />
      </svg>
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function Button({ kind = "quiet", children, ...rest }) {
  return (
    <button type="button" className={`ui-btn ui-btn--${kind}`} {...rest}>
      {children}
    </button>
  );
}

/* ── table ───────────────────────────────────────────────────────────────── */

/**
 * `columns` are headings, `children` the rows.
 *
 * Loading, error and empty are handled here so no screen has to remember all
 * three — the inherited ones variously forgot the empty state, or showed a
 * spinner on top of a populated table.
 */
export function DataTable({
  columns,
  loading,
  error,
  isEmpty,
  emptyTitle = "Nothing here yet",
  emptyHint,
  children,
}) {
  return (
    <>
      {error ? <div className="ui-alert ui-alert--error">{error}</div> : null}
      <div className="ui-table-wrap">
        {loading ? <div className="ui-loading" /> : null}
        <div className="ui-scroll">
          <table className="ui-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isEmpty ? (
                <tr>
                  <td colSpan={columns.length} style={{ padding: 0 }}>
                    <div className="ui-state">
                      <span className="ui-state__title">{emptyTitle}</span>
                      {emptyHint ? (
                        <span className="ui-state__hint">{emptyHint}</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ) : (
                children
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export function Pill({ tone = "grey", children }) {
  return <span className={`ui-pill ui-pill--${tone}`}>{children}</span>;
}

export function Avatar({ label, accent = false }) {
  return (
    <span className={"ui-avatar" + (accent ? " ui-avatar--accent" : "")}>
      {(label || "?").charAt(0).toUpperCase()}
    </span>
  );
}

export function RowActions({ onView, onEdit, onDelete }) {
  return (
    <div className="ui-actions">
      {onView ? (
        <button
          type="button"
          className="ui-icon-btn ui-icon-btn--view"
          onClick={onView}
          title="View"
          aria-label="View"
        >
          <ViewIcon />
        </button>
      ) : null}
      {onEdit ? (
        <button
          type="button"
          className="ui-icon-btn"
          onClick={onEdit}
          title="Edit"
          aria-label="Edit"
        >
          <EditIcon />
        </button>
      ) : null}
      {onDelete ? (
        <button
          type="button"
          className="ui-icon-btn ui-icon-btn--danger"
          onClick={onDelete}
          title="Delete"
          aria-label="Delete"
        >
          <BinIcon />
        </button>
      ) : null}
    </div>
  );
}

/* ── modal ───────────────────────────────────────────────────────────────── */

/**
 * Escape closes it; a backdrop click does NOT.
 *
 * These hold half-filled forms, and losing one to a stray click beside the box
 * is how an admin retypes a user. Escape is deliberate enough to count.
 */
export function Modal({ open, title, onClose, footer, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="ui-backdrop" />
      <div className="ui-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="ui-modal__head">
          <h2 className="ui-modal__title">{title}</h2>
          <button
            type="button"
            className="ui-modal__x"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="ui-modal__body">{children}</div>
        {footer ? <div className="ui-modal__foot">{footer}</div> : null}
      </div>
    </>
  );
}

export function Field({ label, required, hint, children }) {
  return (
    <div className="ui-field">
      <label className="ui-field__label">
        {label}
        {required ? <span> *</span> : null}
      </label>
      {children}
      {hint ? <p className="ui-field__hint">{hint}</p> : null}
    </div>
  );
}


/**
 * On or off.
 *
 * A button, not a checkbox inside a label: the inherited .switch/.slider pair
 * hid an <input> with display:none, which takes it out of the tab order and
 * leaves the control unreachable from the keyboard. This one is focusable,
 * announces its state, and is one element.
 */
export function Toggle({ on, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={"ui-toggle" + (on ? " is-on" : "")}
    />
  );
}

/** A filter dropdown, shaped like the search box beside it. */
export function Filter({ value, onChange, options, ariaLabel }) {
  return (
    <select
      className="ui-filter"
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}


/** A picture in a row, or a plain note that there isn't one. */
export function Thumb({ src, alt }) {
  if (!src) return <span className="ui-thumb ui-thumb--none">none</span>;
  return <img className="ui-thumb" src={src} alt={alt || ""} loading="lazy" />;
}

/**
 * Pick an image, by click or by drop.
 *
 * Takes the file and hands it straight back; cropping, uploading and what the
 * path ends up being are the screen's business, because every screen wants a
 * different aspect ratio and a different folder.
 */
export function ImagePicker({ preview, onPick, hint = "PNG or JPG" }) {
  const [over, setOver] = React.useState(false);
  const input = React.useRef(null);

  const take = (file) => {
    if (file && file.type.startsWith("image/")) onPick(file);
  };

  return (
    <div
      className={
        "ui-drop" + (over ? " is-over" : "") + (preview ? " has-image" : "")
      }
      onClick={() => input.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        take(e.dataTransfer.files[0]);
      }}
    >
      {preview ? (
        <>
          <img src={preview} alt="" />
          <button
            type="button"
            className="ui-drop__swap"
            onClick={(e) => {
              e.stopPropagation();
              input.current?.click();
            }}
          >
            Replace
          </button>
        </>
      ) : (
        <>
          <span className="ui-drop__title">Drop an image, or click to pick</span>
          <span className="ui-drop__hint">{hint}</span>
        </>
      )}
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          take(e.target.files[0]);
          // Cleared so picking the SAME file twice still fires onChange.
          e.target.value = "";
        }}
      />
    </div>
  );
}

/**
 * Page through a list.
 *
 * ── WHY IT ELIDES ──
 * The screens this replaces rendered one button per page from
 * Array.from({ length: totalPages }) — fine at four pages, a second navigation
 * bar at forty. This shows the ends, the neighbours, and a gap.
 */
export function Pager({ page, totalPages, total, perPage, onPage }) {
  if (!totalPages || totalPages < 2) return null;

  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total ?? page * perPage);

  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    const near = Math.abs(p - page) <= 1;
    const edge = p === 1 || p === totalPages;
    if (near || edge) pages.push(p);
    else if (pages[pages.length - 1] !== "gap") pages.push("gap");
  }

  return (
    <div className="ui-pager">
      <span className="ui-pager__where">
        {total != null ? `Showing ${from}–${to} of ${total}` : `Page ${page} of ${totalPages}`}
      </span>
      <div className="ui-pager__steps">
        <button
          type="button"
          className="ui-pager__btn"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Prev
        </button>
        {pages.map((p, i) =>
          p === "gap" ? (
            <span key={`gap${i}`} className="ui-pager__gap">…</span>
          ) : (
            <button
              key={p}
              type="button"
              className={"ui-pager__btn" + (p === page ? " is-current" : "")}
              onClick={() => onPage(p)}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          className="ui-pager__btn"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}


/**
 * A select you can type into.
 *
 * ── WHY NOT A PLAIN <select> ──
 * These pick from the catalogue, and a shop with two hundred products cannot
 * be scrolled to. ── WHY NOT THE THREE COPIES THAT WERE HERE ── the sub
 * category, product and offer screens each carried their own ninety-line
 * version with its own hover handlers and its own idea of the border colour.
 *
 * `options` is [{ id, name }]; `value` is an id.
 */
export function Combo({ value, onChange, options = [], placeholder = "Choose…" }) {
  const [open, setOpen] = React.useState(false);
  const [find, setFind] = React.useState("");
  const box = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    const key = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const chosen = options.find((o) => String(o.id) === String(value));
  const shown = find.trim()
    ? options.filter((o) =>
        (o.name || "").toLowerCase().includes(find.trim().toLowerCase())
      )
    : options;

  return (
    <div className="ui-combo" ref={box}>
      <button
        type="button"
        className={
          "ui-combo__face" +
          (open ? " is-open" : "") +
          (chosen ? "" : " is-empty")
        }
        onClick={() => {
          setOpen((v) => !v);
          setFind("");
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{chosen?.name || placeholder}</span>
        <svg
          className="ui-combo__caret"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 15.6 5.2 8.8a1.2 1.2 0 0 1 1.7-1.7L12 12.2l5.1-5.1a1.2 1.2 0 0 1 1.7 1.7Z" />
        </svg>
      </button>

      {open ? (
        <div className="ui-combo__menu" role="listbox">
          <div className="ui-combo__find">
            <input
              autoFocus
              value={find}
              onChange={(e) => setFind(e.target.value)}
              placeholder="Type to narrow…"
            />
          </div>
          <div className="ui-combo__list">
            {shown.length === 0 ? (
              <p className="ui-combo__none">Nothing matches that.</p>
            ) : (
              shown.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="option"
                  aria-selected={String(o.id) === String(value)}
                  className={
                    "ui-combo__opt" +
                    (String(o.id) === String(value) ? " is-picked" : "")
                  }
                  onClick={() => {
                    onChange(o.id);
                    setOpen(false);
                  }}
                >
                  {o.name}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── glyphs ──────────────────────────────────────────────────────────────── */

export function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.6a1.4 1.4 0 0 1 1.4 1.4v6.6H20a1.4 1.4 0 0 1 0 2.8h-6.6V20a1.4 1.4 0 0 1-2.8 0v-6.6H4a1.4 1.4 0 0 1 0-2.8h6.6V4A1.4 1.4 0 0 1 12 2.6Z" />
    </svg>
  );
}

/* Solid silhouettes, matching the rail. One shape and one edge reads faster at
   this size than an outline, which asks the eye to resolve two edges and the
   gap between them before the shape means anything. */
function ActionGlyph({ children }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/* Look at it — a lens with the pupil punched out, so it still reads as an eye
   at 15px rather than as a filled almond. */
function ViewIcon() {
  return (
    <ActionGlyph>
      <path d="M12 4.6c5 0 9 3.6 10.5 7a1 1 0 0 1 0 .8C21 15.8 17 19.4 12 19.4S3 15.8 1.5 12.4a1 1 0 0 1 0-.8C3 8.2 7 4.6 12 4.6Zm0 3.3a4.1 4.1 0 1 0 0 8.2 4.1 4.1 0 0 0 0-8.2Zm0 2.2a1.9 1.9 0 1 1 0 3.8 1.9 1.9 0 0 1 0-3.8Z" />
    </ActionGlyph>
  );
}

/* Change it — a pencil at an angle with a sharpened tip, not the
   square-with-a-pen that every template ships. */
function EditIcon() {
  return (
    <ActionGlyph>
      <path d="m16.6 2.9 4.5 4.5-11 11a1.6 1.6 0 0 1-.8.4l-5 1a1 1 0 0 1-1.2-1.2l1-5a1.6 1.6 0 0 1 .4-.8Zm5.9 3.1-4.5-4.5.8-.8a2.2 2.2 0 0 1 3.1 0l1.4 1.4a2.2 2.2 0 0 1 0 3.1Z" />
    </ActionGlyph>
  );
}

/* Remove it — a bin, lid on, ribs cut out of the body. */
function BinIcon() {
  return (
    <ActionGlyph>
      <path d="M9.6 2.2h4.8a1.6 1.6 0 0 1 1.6 1.6v1.4h4.6a1 1 0 0 1 0 2h-.5l-1 12.4a2.4 2.4 0 0 1-2.4 2.2H7.3a2.4 2.4 0 0 1-2.4-2.2l-1-12.4h-.5a1 1 0 0 1 0-2h4.6V3.8a1.6 1.6 0 0 1 1.6-1.6Zm.4 3h4V4.2h-4Zm-.1 5a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Zm4.2 0a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Z" />
    </ActionGlyph>
  );
}
